import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { Logo } from '../../../components/Logo';
import { BrandingContext } from '../../../context/BrandingContext';
import { useToastStore } from '../../../stores/toastStore';
import {
  ConfigQueryKeys,
  getBranding,
  updateBranding,
  type TenantBrandingResponse,
  type UpdateBrandingBody,
} from '../api';

const HEX = /^#[0-9a-fA-F]{6}$/;
const DEFAULT_PRODUCT = 'DocuSense';
const DEFAULT_PRIMARY = '#046bd2';
const DEFAULT_LOGO_HEIGHT = 32;
const MIN_LOGO_HEIGHT = 16;
const MAX_LOGO_HEIGHT = 64;

/**
 * Tenant white-label branding (M1): product name, primary + accent colours, logo (URL + height) and
 * favicon applied to the panel. Saving writes through the shared `ConfigQueryKeys.branding` cache so
 * the app shell (Layout) re-themes live. "Restablecer a DocuSense" reverts to the baseline — it PUTs
 * the default values, so it works despite the product name being required.
 */
export function BrandingSection(): JSX.Element {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((s) => s.push);
  const { data, isLoading, isError } = useQuery({
    queryKey: ConfigQueryKeys.branding,
    queryFn: ({ signal }) => getBranding(signal),
  });

  const [productName, setProductName] = useState('');
  const [color, setColor] = useState(DEFAULT_PRIMARY);
  const [logoUrl, setLogoUrl] = useState('');
  const [secondaryColor, setSecondaryColor] = useState('');
  const [logoHeight, setLogoHeight] = useState(DEFAULT_LOGO_HEIGHT);
  const [faviconUrl, setFaviconUrl] = useState('');
  const [error, setError] = useState<string | null>(null);

  function syncFrom(b: TenantBrandingResponse): void {
    setProductName(b.productName);
    setColor(b.primaryColorHex);
    setLogoUrl(b.logoUrl ?? '');
    setSecondaryColor(b.secondaryColorHex ?? '');
    setLogoHeight(b.logoHeightPx);
    setFaviconUrl(b.faviconUrl ?? '');
    setError(null);
  }

  useEffect(() => {
    if (data !== undefined) {
      syncFrom(data);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const mutation = useMutation({
    mutationFn: (body: UpdateBrandingBody) => updateBranding(body),
    onSuccess: (updated: TenantBrandingResponse) => {
      queryClient.setQueryData(ConfigQueryKeys.branding, updated);
      syncFrom(updated);
      pushToast({ tone: 'success', title: 'Marca actualizada' });
    },
    onError: (e: unknown) => setError(e instanceof Error ? e.message : 'No se pudo guardar la marca.'),
  });

  const colorValid = HEX.test(color);
  const secondaryValid = secondaryColor.trim() === '' || HEX.test(secondaryColor);
  const canSave = productName.trim() !== '' && colorValid && secondaryValid && !mutation.isPending;

  function save(): void {
    mutation.mutate({
      productName: productName.trim(),
      primaryColorHex: color,
      logoUrl: logoUrl.trim() === '' ? null : logoUrl.trim(),
      secondaryColorHex: secondaryColor.trim() === '' ? null : secondaryColor.trim(),
      logoHeightPx: logoHeight,
      faviconUrl: faviconUrl.trim() === '' ? null : faviconUrl.trim(),
    });
  }

  function resetToDefault(): void {
    mutation.mutate({
      productName: DEFAULT_PRODUCT,
      primaryColorHex: DEFAULT_PRIMARY,
      logoUrl: null,
      secondaryColorHex: null,
      logoHeightPx: DEFAULT_LOGO_HEIGHT,
      faviconUrl: null,
    });
  }

  if (isLoading) {
    return <div className="flex justify-center py-8"><LoadingSpinner /></div>;
  }
  if (isError) {
    return <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">No se pudo cargar la marca.</div>;
  }

  return (
    <div className="card max-w-2xl space-y-4 p-5" data-testid="branding-section">
      <div>
        <h2 className="text-base font-semibold text-slate-900">Marca del panel</h2>
        <p className="mt-1 text-sm text-slate-500">
          Personaliza el nombre, los colores, el logo y el favicon que ven tus usuarios en el panel.
        </p>
      </div>

      <label className="block text-sm text-slate-700">
        Nombre del producto
        <input
          className="input mt-1"
          value={productName}
          onChange={(e) => setProductName(e.target.value)}
          maxLength={60}
          placeholder="DocuSense"
          data-testid="branding-name"
        />
      </label>

      <div className="flex items-center gap-3 text-sm text-slate-700">
        <span className="w-28">Color principal</span>
        <input
          type="color"
          value={colorValid ? color : DEFAULT_PRIMARY}
          onChange={(e) => setColor(e.target.value)}
          className="h-8 w-12 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
          aria-label="Color principal"
          data-testid="branding-color"
        />
        <input
          className="input max-w-[8rem] font-mono text-xs"
          value={color}
          onChange={(e) => setColor(e.target.value)}
          aria-label="Color principal (hex)"
          data-testid="branding-color-hex"
        />
        {!colorValid ? <span className="text-xs text-rose-700">Formato #RRGGBB</span> : null}
      </div>

      <div className="flex items-center gap-3 text-sm text-slate-700">
        <span className="w-28">Color de acento</span>
        <input
          type="color"
          value={HEX.test(secondaryColor) ? secondaryColor : color}
          onChange={(e) => setSecondaryColor(e.target.value)}
          className="h-8 w-12 cursor-pointer rounded border border-slate-300 bg-white p-0.5"
          aria-label="Color de acento"
          data-testid="branding-secondary"
        />
        <input
          className="input max-w-[8rem] font-mono text-xs"
          value={secondaryColor}
          onChange={(e) => setSecondaryColor(e.target.value)}
          placeholder="(usa el principal)"
          aria-label="Color de acento (hex)"
          data-testid="branding-secondary-hex"
        />
        {secondaryColor.trim() !== '' ? (
          <button type="button" className="text-xs text-slate-500 underline" onClick={() => setSecondaryColor('')}>
            quitar
          </button>
        ) : null}
        {!secondaryValid ? <span className="text-xs text-rose-700">Formato #RRGGBB</span> : null}
      </div>

      <label className="block text-sm text-slate-700">
        URL del logo (opcional)
        <input
          className="input mt-1"
          value={logoUrl}
          onChange={(e) => setLogoUrl(e.target.value)}
          placeholder="https://tu-dominio.com/logo.png"
          data-testid="branding-logo"
        />
      </label>

      <label className="block text-sm text-slate-700">
        Tamaño del logo: <span className="font-mono">{logoHeight}px</span>
        <input
          type="range"
          min={MIN_LOGO_HEIGHT}
          max={MAX_LOGO_HEIGHT}
          value={logoHeight}
          onChange={(e) => setLogoHeight(Number(e.target.value))}
          className="mt-1 w-full accent-primary"
          aria-label="Tamaño del logo"
          data-testid="branding-logo-height"
        />
        <span className="text-xs text-slate-400">Aplica a tu logo (no a la marca por defecto).</span>
      </label>

      <label className="block text-sm text-slate-700">
        URL del favicon (opcional)
        <input
          className="input mt-1"
          value={faviconUrl}
          onChange={(e) => setFaviconUrl(e.target.value)}
          placeholder="https://tu-dominio.com/favicon.ico"
          data-testid="branding-favicon"
        />
      </label>

      {/* Live preview using the same Logo component, fed the in-progress values, plus an accent swatch. */}
      <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
        <p className="mb-2 text-xs font-medium uppercase tracking-wide text-slate-500">Vista previa</p>
        <div className="flex items-center justify-between gap-4">
          <BrandingContext.Provider
            value={{
              productName: productName.trim() === '' ? DEFAULT_PRODUCT : productName.trim(),
              logoUrl: logoUrl.trim() === '' ? null : logoUrl.trim(),
              logoHeightPx: logoHeight,
            }}
          >
            <Logo size={28} />
          </BrandingContext.Provider>
          <span
            className="h-6 w-6 flex-none rounded-full border border-slate-300"
            style={{ backgroundColor: HEX.test(secondaryColor) ? secondaryColor : color }}
            title="Color de acento"
            aria-hidden
          />
        </div>
      </div>

      {error !== null ? <p className="text-xs text-rose-700">{error}</p> : null}

      <div className="flex items-center gap-3">
        <button
          type="button"
          className="btn-primary"
          onClick={save}
          disabled={!canSave}
          data-testid="save-branding"
        >
          {mutation.isPending ? 'Guardando…' : 'Guardar marca'}
        </button>
        <button
          type="button"
          className="text-sm text-slate-600 underline hover:text-slate-900 disabled:opacity-50"
          onClick={resetToDefault}
          disabled={mutation.isPending}
          data-testid="reset-branding"
        >
          Restablecer a DocuSense
        </button>
      </div>
    </div>
  );
}
